import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";

import {
  config,
  language,
  ACCOUNT_STATUSES,
  FEATURE_FLAGS,
  BRAND_NAME,
  BRAND_PATHS,
  CONSENT_IDS,
  PRODUCT_TYPE_IDS,
} from "@chef/constants";
import { capitalize } from "@chef/utils/string";
import {
  getWeek,
  getYear,
  isAccountPaused,
  isMealboxProduct,
  isPickAndMixProduct,
  nextDeliveryWeekDateObj,
  createReactivationSchedulers,
  isGroceryBasketType,
  isMealboxBasketType,
} from "@chef/helpers";
import {
  useCalendarData,
  useExtendedBasketData,
  useFavorites,
  usePickAndMixPrice,
  useUpdateBasketPickAndMixDeviation,
  useAppDispatch,
  usePreselector,
  useTrack,
  useLoyalty,
} from "@chef/state-management/hooks";
import {
  useBillingQuery,
  useUserInfoQuery,
  loadCalendar,
  selectDeviationProducts,
  selectHasSetInitialPrice,
  setDeviation,
  setHasSetInitialPrice,
  AppDispatch,
  PickAndMixQuery,
  selectDeviationBasket,
  showNotification,
  useCreateOrUpdateDeviationByBasketIdMutation,
  useCreateOrUpdateMultipleSchedulersMutation,
  useRecommendedRecipesQuery,
  usePickAndMixQuery,
  useUpdateBillingAgreementMutation,
  useValidateBasketMutation,
  useCreateOrUpdateSchedulerMutation,
  toggleEditModeForWeek,
} from "@chef/state-management";
import { isEqualStrings, isNotEqualStrings } from "@chef/utils/equal";
import {
  getCurrentWeekDataAndBasketFromCalendar,
  getVariationFromPickAndMix,
  isVariationPlusPortions,
  compareBasketsProducts,
  getBasketFinances,
  getBasketForCalendarWeek,
  getWeeklyTimeblockDataFromCalendar,
} from "@chef/state-management/helpers";
import { nonNullable } from "@chef/utils/nonNullable";
import { useEffectOnce } from "@chef/hooks";
import { isNotEmptyArray } from "@chef/utils/array";
import { useVariant } from "@chef/feature-flags";
import { useDebounce } from "@chef/hooks";
import { isEmptyArray, sumByProperty } from "@chef/utils/array";

import type { WeekYear } from "@chef/types/common";

import { MyMenuCardProps } from "./lib/WeekOverviewExperimental/MyMenuCard";
import { filterBasketItems } from "./utils";

type SortWeights = {
  IN_BASKET: number;
  FAVORITED: number;
  DISLIKED: number;
  PRICE: number;
  RECOMMENDATION: number;
};

export type MyMenuCardData =
  | {
      key: string;
      type: "upsale";
      img: string;
    }
  | {
      key: string;
      type: "recipe";
      recipe: PickAndMixQuery["pickAndMix"][number]["recipes"][number];
      productId: string;
    };

export const useCreateDeviationBasket = (args: {
  week: number;
  year: number;
}) => {
  const { week, year } = args;

  const currentDate = nextDeliveryWeekDateObj();
  const currentWeek = getWeek(currentDate);
  const currentYear = getYear(currentDate);

  const dispatch = useDispatch<AppDispatch>();

  const { getPriceOfPickAndMixProduct } = usePickAndMixPrice({
    week,
    year,
  });

  // load calendar
  useEffect(() => {
    dispatch(loadCalendar({ week: currentWeek, year: currentYear, range: 4 }));
  }, [currentWeek, currentYear, dispatch]);

  // selectors
  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const hasSetInitialPrice = useSelector(
    selectHasSetInitialPrice({ week, year }),
  );

  useEffect(() => {
    // set initial price data (it won't be correct when we fetch it from calendar)
    if (hasSetInitialPrice) {
      return;
    }
    if (!selectedProducts) {
      return;
    }

    dispatch(setHasSetInitialPrice({ week, year, set: true }));

    const newDeviation = selectedProducts.map((sp) => {
      if (!isPickAndMixProduct(sp)) {
        return sp;
      }

      return {
        ...sp,
        price:
          getPriceOfPickAndMixProduct({
            productId: sp.productId,
            variationId: sp.variationId,
          }) || 0,
      };
    });

    dispatch(
      setDeviation({
        basket: {
          products: newDeviation,
          week,
          year,
        },
      }),
    );
  }, [selectedProducts, week, year]);

  const isLoading = selectedProducts === undefined;

  return {
    isLoading,
  };
};

interface UsePickAndMixProductWithPrice {
  sortedPickAndMix: PickAndMixQuery["pickAndMix"];
  week: number;
  year: number;
  canSelectProducts?: boolean;
}

export const usePickAndMixProductWithPrice = ({
  sortedPickAndMix,
  week,
  year,
  canSelectProducts = true,
}: UsePickAndMixProductWithPrice) => {
  const { getPriceOfPickAndMixProduct, isSuccess: hasLoadedPrices } =
    usePickAndMixPrice({
      week,
      year,
    });

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const {
    data: extendedBasketData,
    isLoading: isLoadingExtendedBasketData,
    isError: isErrorExtendedBasketData,
  } = useExtendedBasketData({ week, year, skip: !canSelectProducts });

  let isReady: boolean;

  if (canSelectProducts) {
    isReady = hasLoadedPrices;
  } else {
    isReady = true;
  }

  const pickAndMixProductWithPrice = sortedPickAndMix
    .flatMap((pnm) =>
      pnm.recipes.flatMap(() => {
        const variationIds = sortedPickAndMix.flatMap((pickAndMix) =>
          pickAndMix.recipes.flatMap((recipe) => {
            return recipe.portions
              .filter((p) => p.size === extendedBasketData.portions)
              .map((p) => p.variationId);
          }),
        );

        const { product } = pnm;

        const variations = variationIds
          .map((variationId) =>
            getVariationFromPickAndMix({
              pickAndMix: sortedPickAndMix,
              productId: product.productId,
              variationId: variationId,
            }),
          )
          .filter((variation) => variation !== null);

        const plusProteinVariation = variations.find((variation) =>
          isVariationPlusPortions(variation || { attributes: [] }),
        );

        const variation = variations.find(
          (v) => !isVariationPlusPortions(v || { attributes: [] }),
        );

        // if the variation don't exist, it means it was filtered out by filterPickAndMix
        if (!variation || (!selectedProducts && canSelectProducts)) {
          return null;
        }

        const plusVariationIsInBasket = selectedProducts?.some((p) =>
          isEqualStrings(p.variationId, plusProteinVariation?.variationId),
        );

        const variationIsInBasket = selectedProducts?.some((p) =>
          isEqualStrings(p.variationId, variation?.variationId),
        );

        const hasPlusProtein = !!plusProteinVariation;

        const productIsInBasket =
          variationIsInBasket || plusVariationIsInBasket;

        const variationId = plusVariationIsInBasket
          ? plusProteinVariation?.variationId || ""
          : variation.variationId;

        const price = getPriceOfPickAndMixProduct({
          variationId,
          product,
        });

        const recipes = pnm.recipes.map((recipe) => {
          return {
            ...recipe,
            price: price ?? 0,
            variation,
            productIsInBasket,
            hasPlusProtein,
            plusVariationIsInBasket,
          };
        });

        return {
          ...pnm,
          recipes,
        };
      }),
    )
    .filter(nonNullable);

  const negativePriceRecipes = pickAndMixProductWithPrice
    .flatMap((p) => p.recipes)
    .filter((r) => r.price < 0);

  return {
    pickAndMix: isReady ? pickAndMixProductWithPrice : [],
    negativePriceRecipes,
    isLoadingExtendedBasketData,
    isErrorExtendedBasketData,
  };
};

type PickAndMixWithPrice = ReturnType<
  typeof usePickAndMixProductWithPrice
>["pickAndMix"];

interface UseSortedPickAndMix {
  pickAndMix: PickAndMixWithPrice;
  week: number;
  year: number;
}

export const useSortedPickAndMix = ({
  pickAndMix,
  week,
  year,
}: UseSortedPickAndMix) => {
  const { variant, isReady: isFlagReady } = useVariant(
    FEATURE_FLAGS.WEEK_EDITOR_SORTING,
  );

  const [sortedPickAndMix, setSortedPickAndMix] = useState<PickAndMixWithPrice>(
    [],
  );

  const { isDisliked, isFavorited, isReady: isFavoritesReady } = useFavorites();

  const {
    data: { selectedProducts, portions },
    isLoading: isLoadingExtendedBasketData,
  } = useExtendedBasketData({ week, year });

  const { data: recommendedRecipesQuery, isLoading: isLoadingRecommendations } =
    useRecommendedRecipesQuery({
      week,
      year,
    });

  const recommendations =
    recommendedRecipesQuery?.recommendedRecipes.recipes ?? [];

  const maxRecipePrice = Math.max(
    ...pickAndMix.map((pmx) => pmx.recipes[0].price),
  );

  const normalize = (val: number, min: number, max: number) => {
    if (min === max) {
      return 0;
    }

    return (val - min) / (max - min);
  };

  const score = (pmx: PickAndMixWithPrice[number], weights: SortWeights) => {
    const recipe = pmx.recipes[0];

    const isInBasket =
      recipe.productIsInBasket || recipe.plusVariationIsInBasket || false;

    const _isDisliked = isDisliked(recipe);
    const _isFavorited = isFavorited(recipe);

    const recommendationScore =
      recommendations.find(
        (r) =>
          r.mainRecipeId === recipe.mainRecipeId ||
          r.mainRecipeId === recipe.recipeId,
      )?.score || 0;

    let score = 0;

    const scores = {
      IN_BASKET: 0,
      FAVORITED: 0,
      DISLIKED: 0,
      PRICE: 0,
      RECOMMENDATION: 0,
    };

    if (isInBasket) {
      score += weights.IN_BASKET;

      scores.IN_BASKET = weights.IN_BASKET;
    }

    if (_isFavorited) {
      score += weights.FAVORITED;

      scores.FAVORITED = weights.FAVORITED;
    }

    if (_isDisliked) {
      score += weights.DISLIKED;

      scores.DISLIKED = weights.DISLIKED;
    }

    const normalizedRecommendationScore =
      normalize(recommendationScore, 0, 2) * weights.RECOMMENDATION;

    score += normalizedRecommendationScore;

    scores.RECOMMENDATION = normalizedRecommendationScore;

    const normalizedPriceScore =
      normalize(Math.abs(recipe.price), 0, maxRecipePrice) * weights.PRICE;

    score += normalizedPriceScore;

    scores.PRICE = normalizedPriceScore;

    return [pmx, scores, score] as const;
  };

  // We want pickAndMix to be sorted in the following order. After a finished sort, we want to lock the order,
  // so that the user can't change it by clicking on the pickAndMix products.
  // 1. Selected products
  // 2. Dishes with 0 cost
  // 3. Dishes with negative cost, sorted by price in ascending order
  // 4. Dishes with positive cost, sorted by price in ascending order
  // 5. Disliked recipes
  const compareFn = (
    a: PickAndMixWithPrice[number],
    b: PickAndMixWithPrice[number],
    withRecommendations = false,
  ) => {
    const aRecipe = a.recipes[0];
    const bRecipe = b.recipes[0];

    const aIsInBasket =
      aRecipe.productIsInBasket || aRecipe.plusVariationIsInBasket;

    const bIsInBasket =
      bRecipe.productIsInBasket || bRecipe.plusVariationIsInBasket;

    if (aIsInBasket && !bIsInBasket) {
      return -1;
    }

    if (!aIsInBasket && bIsInBasket) {
      return 1;
    }

    const aIsDisliked = isDisliked(aRecipe);
    const bIsDisliked = isDisliked(bRecipe);

    const aIsFavorited = isFavorited(aRecipe);
    const bIsFavorited = isFavorited(bRecipe);

    const aIsPicked = selectedProducts?.some((p) =>
      isEqualStrings(p.productId, a.productId),
    );

    const bIsPicked = selectedProducts?.some((p) =>
      isEqualStrings(p.productId, b.productId),
    );

    if (aIsPicked && !bIsPicked) {
      return -1;
    }

    if (!aIsPicked && bIsPicked) {
      return 1;
    }

    if (aIsDisliked && !bIsDisliked) {
      return 1;
    }

    if (!aIsDisliked && bIsDisliked) {
      return -1;
    }

    if (aRecipe.price === 0 && bRecipe.price !== 0) {
      return -1;
    }

    if (aRecipe.price !== 0 && bRecipe.price === 0) {
      return 1;
    }

    if (aRecipe.price < 0 && bRecipe.price > 0) {
      return -1;
    }

    if (aRecipe.price > 0 && bRecipe.price < 0) {
      return 1;
    }

    if (aRecipe.price === bRecipe.price && aIsFavorited && !bIsFavorited) {
      return -1;
    }

    if (aRecipe.price === bRecipe.price && !aIsFavorited && bIsFavorited) {
      return 1;
    }

    if (withRecommendations) {
      if (aRecipe.price !== bRecipe.price) {
        return aRecipe.price - bRecipe.price;
      }

      const aRecommendationScore =
        recommendations.find(
          (r) =>
            r.mainRecipeId === aRecipe.mainRecipeId ||
            r.mainRecipeId === aRecipe.recipeId,
        )?.score || 0;

      const bRecommendationScore =
        recommendations.find(
          (r) =>
            r.mainRecipeId === bRecipe.mainRecipeId ||
            r.mainRecipeId === bRecipe.recipeId,
        )?.score || 0;

      return bRecommendationScore - aRecommendationScore;
    }

    return aRecipe.price - bRecipe.price;
  };

  useEffectOnce(
    () => {
      let sortingMethod = "control";

      if (variant?.enabled) {
        sortingMethod = variant?.name ?? "control";
      }

      if (sortingMethod === "control") {
        console.log("using control");

        setSortedPickAndMix([...pickAndMix].sort((a, b) => compareFn(a, b)));
      } else if (sortingMethod === "control_with_recommendations") {
        console.log("using control_with_recommendations");

        setSortedPickAndMix(
          [...pickAndMix].sort((a, b) => compareFn(a, b, true)),
        );
      } else if (variant?.name === "weighted") {
        let weights: SortWeights;
        console.log("using weighted");
        try {
          weights = JSON.parse(variant?.payload?.value || "");
        } catch (error) {
          console.error("Error parsing weights:", error);

          weights = {
            IN_BASKET: 1 << 10,
            FAVORITED: 1 << 9,
            DISLIKED: -1 << 9,
            PRICE: -1 << 8,
            RECOMMENDATION: 1 << 7,
          };
        }

        console.group("Weights");
        console.table(weights);

        const scores = pickAndMix.map((pmx) => score(pmx, weights));
        const sorted = [...scores].sort((a, b) => b[2] - a[2]);

        console.table(
          sorted.map(([pmx, scores, total]) => ({
            recipeId: pmx.recipes[0].recipeId,
            recipeName: pmx.recipes[0].recipeName,
            ...scores,
            total,
          })),
        );

        console.groupEnd();

        setSortedPickAndMix(sorted.map(([pmx]) => pmx));
      }
    },
    !isLoadingExtendedBasketData &&
      isFavoritesReady &&
      isFlagReady &&
      !isLoadingRecommendations &&
      isNotEmptyArray(pickAndMix),
    portions,
  );

  return { sortedPickAndMix };
};

interface UseScrollIntoViewHighlightOptions {
  dataAttribute?: string;
  scrollBehavior?: ScrollBehavior;
  highlight?: boolean;
  highlightClassNames?: string[];
  duration?: number;
  shouldScroll?: boolean;
}
/**
 * useScrollIntoViewHighlight
 *
 * A React hook that scrolls an element into view and applies a temporary highlight effect,
 * typically used when navigating via query parameters like `?recipeId=123`.
 *
 * The element is selected via a `data-*` attribute matching the provided `id`.
 * This is useful for jumping to a specific card or section on initial page load.
 *
 * @param id - The ID of the element to scroll to (e.g. from a URL query param). If `undefined`, the hook does nothing.
 * @param options - Configuration options:
 *   - dataAttribute (string): The `data-*` attribute used to identify the element. Defaults to `"data-recipe-id"`.
 *   - scrollBehavior: Controls the scroll animation. Defaults to `"smooth"`.
 *   - highlight (boolean): Whether to apply a temporary highlight ring to the element. Defaults to `true`.
 *   - highlightClassNames (string[]): The CSS classes applied during highlight. Defaults to `["ring", "ring-highlight", "transition"]`.
 *   - duration (number): Duration (in ms) the highlight stays before being removed. Defaults to `2000`.
 *   - shouldScroll (boolean): Controls whether the scroll and highlight logic should run. This is useful for deferring execution
 *     until the DOM is ready — for example, after recipe cards have been rendered. If `false`, the hook does nothing. Defaults to false

 */
export const useScrollIntoViewHighlight = (
  id: string | undefined,
  {
    dataAttribute = "data-recipe-id",
    scrollBehavior = "smooth",
    highlight = true,
    highlightClassNames = ["ring", "ring-highlight", "transition"],
    duration = 2000,
    shouldScroll = false,
  }: UseScrollIntoViewHighlightOptions = {},
) => {
  useEffect(() => {
    if (!id || !shouldScroll) {
      return;
    }

    const selector = `[${dataAttribute}="${id}"]`;
    const element = document.querySelector(selector) as HTMLElement | null;

    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: scrollBehavior, block: "center" });

    if (highlight) {
      element.classList.add(...highlightClassNames);
      setTimeout(() => {
        element.classList.remove(...highlightClassNames);
      }, duration);
    }
  }, [id, dataAttribute, shouldScroll]);
};

export const usePersonalGreeting = () => {
  const { data: userInfoQuery } = useUserInfoQuery();

  const now = new Date();
  const hour = now.getHours();

  let greeting = "";

  if (hour < 10) {
    greeting = intl.GOOD_MORNING;
  } else if (hour < 14) {
    greeting = intl.GOOD_DAY;
  } else if (hour < 18) {
    greeting = intl.GOOD_AFTERNOON;
  } else {
    greeting = intl.GOOD_EVENING;
  }

  if (!userInfoQuery) {
    return greeting + "!";
  }

  const name = capitalize(userInfoQuery.userInfo.firstName);

  return `${greeting} ${name}!`;
};

/**
 * Custom hook to retrieve and manage data for "My Menu" cards for a given week and year.
 *
 * This hook aggregates data from multiple sources including calendar, billing, basket, and pickAndMix queries.
 * It provides information about the current delivery status, selected products, meals, portions, and recipe cards.
 * It also exposes a handler to remove a recipe from the basket.
 *
 * @param {WeekYear} params - The week and year for which to fetch menu data.
 * @returns {{
 *   basketMealbox: Product | undefined;
 *   meals: Meal[] | undefined;
 *   portions: Portion[] | undefined;
 *   recipeCards: MyMenuCardData[];
 *   pickAndMix: Product[];
 *   deliveryStatus: MyMenuCardProps["deliveryStatus"];
 *   isOrderGenerated: boolean;
 *   handleRemoveRecipe: (productId: string) => void;
 * }}
 *
 * @remarks
 * - Determines delivery status based on account and delivery information.
 * - Filters and maps products to recipe cards for display.
 * - Handles removal of recipes from the basket.
 * - Uses memoization for performance optimization.
 */
export const useMyMenuCardsData = ({ week, year }: WeekYear) => {
  const { calendar, pickAndMix, basket } = useCalendarData({ week, year });
  const { data: billingQuery } = useBillingQuery();
  const { data: extendedBasketData } = useExtendedBasketData({ week, year });
  const { updateBasketPickAndMixDeviation } =
    useUpdateBasketPickAndMixDeviation({ week, year });

  const currentWeekObject = getCurrentWeekDataAndBasketFromCalendar({
    calendar: calendar || [],
    week,
    year,
  });

  const isBeforeStartDateWeek = currentWeekObject?.isBeforeStartDateWeek;
  const hasDelivery =
    currentWeekObject?.mealboxBasket?.shippingDetails?.hasDelivery ?? false;
  const deliveryPlanned = hasDelivery && !isBeforeStartDateWeek;

  const isPausedAccount = isAccountPaused(billingQuery?.billing.status || 10);

  const deliveryStatus = useMemo<MyMenuCardProps["deliveryStatus"]>(() => {
    if (!deliveryPlanned) {
      return "paused";
    }

    if (isPausedAccount) {
      return "freezed";
    }

    return "active";
  }, [deliveryPlanned, isPausedAccount]);

  const basketMealbox =
    extendedBasketData?.selectedProducts?.find(isMealboxProduct);
  const hasValidGeneratedBasket =
    basket.mealboxBasket.filter(isPickAndMixProduct).length >=
    config.order.minMeals;

  const meals = extendedBasketData?.meals;
  const portions = extendedBasketData?.portions;

  const handleRemoveRecipe = (productId: string) => {
    if (!extendedBasketData?.selectedProducts || !portions) {
      return;
    }

    updateBasketPickAndMixDeviation({
      basketProducts: extendedBasketData.selectedProducts.filter((product) =>
        isNotEqualStrings(product.productId, productId),
      ),
      portions,
    });
  };

  const productsIds = useMemo(() => {
    return extendedBasketData?.selectedProducts
      ?.filter((p) => isPickAndMixProduct(p) || isMealboxProduct(p))
      .map((p) => p.productId);
  }, [extendedBasketData?.selectedProducts]);

  const products =
    pickAndMix?.filter((p) => productsIds?.includes(p.productId)) ?? [];

  const recipeCards = products.flatMap((p) =>
    p.recipes.map((recipe) => ({
      key: `recipe-${recipe.recipeId}`,
      type: "recipe" as const,
      recipe: { ...recipe },
      productId: p.productId,
    })),
  );

  const isSelectedProductsReady = isNotEmptyArray(
    extendedBasketData?.selectedProducts || [],
  );

  return {
    basketMealbox,
    meals,
    portions,
    recipeCards,
    pickAndMix,
    deliveryStatus,
    isOrderGenerated: hasValidGeneratedBasket,
    handleRemoveRecipe,
    isSelectedProductsReady,
  };
};

export const useSubmitDeviation = ({ week, year }: WeekYear) => {
  const router = useRouter();
  const track = useTrack();
  const dispatch = useAppDispatch();
  const { trigger } = usePreselector();

  const deviationBasketForCurrentWeekYear = useSelector(
    selectDeviationBasket({ week, year }),
  );

  const deviationProductsForCurrentWeekYear =
    deviationBasketForCurrentWeekYear?.products;

  const [
    createOrUpdateDeviationByBasketId,
    { isLoading: isLoadingCreateOrUpdateDeviation },
  ] = useCreateOrUpdateDeviationByBasketIdMutation();

  const [
    createOrUpdateMultipleSchedulersMutation,
    { isLoading: isLoadingCreateOrUpdateMultipleSchedulers },
  ] = useCreateOrUpdateMultipleSchedulersMutation();

  const [validateBasketMutation, { isLoading: isLoadingValidateBasket }] =
    useValidateBasketMutation();

  const [
    updateBillingAgreementMutation,
    { isLoading: isLoadingUpdateBillingAgreementMutation },
  ] = useUpdateBillingAgreementMutation();

  const [createOrUpdateScheduler] = useCreateOrUpdateSchedulerMutation();

  const isConfirmDeviationLoading =
    isLoadingUpdateBillingAgreementMutation ||
    isLoadingCreateOrUpdateDeviation ||
    isLoadingCreateOrUpdateMultipleSchedulers ||
    isLoadingValidateBasket;

  const isSubmitDeviationLoading =
    useDebounce(isConfirmDeviationLoading, 50) || isConfirmDeviationLoading;

  const { data: billingData } = useBillingQuery();

  const { data: loyaltyData } = useLoyalty();

  const { basket, calendar, refetchCalendar } = useCalendarData({
    week,
    year,
  });

  const { data: { pickAndMix = [] } = {} } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const calendarWeek = calendar?.filter(
    (c) => c.week === week && c.year === year,
  );

  const timeblock =
    calendarWeek &&
    getWeeklyTimeblockDataFromCalendar({
      calendar: calendarWeek,
      week,
      year,
    });

  const finances =
    deviationProductsForCurrentWeekYear &&
    getBasketFinances({
      basketProducts: deviationProductsForCurrentWeekYear,
      customerFee: timeblock?.customerFee,
      loyaltyLevel: loyaltyData?.currentLevel,
    });

  const calendarMealboxBaskets = calendar?.map(
    (c) =>
      getBasketForCalendarWeek({
        calendarWeek: c,
      }).mealboxBasket?.basketDetails.products,
  );

  const filteredCalendarMealboxBaskets = calendarMealboxBaskets?.map(
    (calendarMealboxBasket) =>
      calendarMealboxBasket?.filter((product) =>
        isEqualStrings(
          product.variation.product.productTypeId,
          PRODUCT_TYPE_IDS.PICKANDMIX,
        ),
      ),
  );

  const isSomeUpcomingDeviationEmpty = filteredCalendarMealboxBaskets?.some(
    (udm) => udm && isEmptyArray(udm),
  );

  const handleSubmitDeviation = async () => {
    if (
      !deviationProductsForCurrentWeekYear ||
      !pickAndMix ||
      !billingData?.billing
    ) {
      throw new Error("Data not initialized");
    }

    const calendarWeekData =
      calendarWeek &&
      getCurrentWeekDataAndBasketFromCalendar({
        calendar: calendarWeek,
        week,
        year,
      });

    const isBeforeStartDateWeek = calendarWeekData?.isBeforeStartDateWeek;

    if (isBeforeStartDateWeek) {
      showNotification({
        type: "error",
        message: intl.NO_DELIVERIES_BEFORE_START_WEEK,
      });
      router.push(BRAND_PATHS.MY_MENU_PAGE);
      return;
    }

    await validateBasketMutation(
      deviationProductsForCurrentWeekYear.map((bp) => ({
        quantity: bp.quantity,
        variationId: bp.variationId,
      })),
    ).unwrap();

    const isPausedAccount = isAccountPaused(billingData.billing.status || 10);

    if (isPausedAccount) {
      await updateBillingAgreementMutation({
        status: ACCOUNT_STATUSES.ACTIVE,
        _suppressNotification: true,
      }).unwrap();

      await createOrUpdateMultipleSchedulersMutation({
        schedulers: createReactivationSchedulers(week, year),
        _suppressNotifications: true,
      }).unwrap();
    }

    const { mealboxBasketItems, groceryBasketItems } = filterBasketItems(
      deviationProductsForCurrentWeekYear,
    );

    const groceryBasketId =
      billingData.billing.baskets.find(isGroceryBasketType)?.basketId;

    const weekIsActive =
      calendarWeekData?.mealboxBasket?.shippingDetails.hasDelivery;

    const mealboxBasketId =
      billingData.billing.baskets.find(isMealboxBasketType)?.basketId;

    const isMealboxBasketChanged = !compareBasketsProducts(
      basket.mealboxBasket,
      mealboxBasketItems,
    );

    const isGroceryBasketChanged = !compareBasketsProducts(
      basket.groceryBasket,
      groceryBasketItems,
    );

    if (mealboxBasketId && isMealboxBasketChanged) {
      await createOrUpdateDeviationByBasketId({
        basketId: mealboxBasketId,
        week,
        year,
        basketDeviationProducts: mealboxBasketItems.map((bp) => ({
          variationId: bp.variationId,
          quantity: bp.quantity,
        })),
      }).unwrap();
    }

    if (groceryBasketId && isGroceryBasketChanged) {
      await createOrUpdateDeviationByBasketId({
        basketId: groceryBasketId,
        week,
        year,
        basketDeviationProducts: groceryBasketItems.map((bp) => ({
          variationId: bp.variationId,
          quantity: bp.quantity,
        })),
      }).unwrap();
    }

    await refetchCalendar().unwrap();

    track("deviationUpdateStepCompleted", {
      affiliation: "Frontend process",
      delivery_update_step: 3,
      delivery_update_step_name: "userConfirmation",
      delivery_update_step_title: intl.ORDER,
    });

    track("deviationCreated", {
      affiliation: "Frontend process",
      source: "week_editor",
      is_active: true,
      products: deviationProductsForCurrentWeekYear.map((bp) => ({
        product_id: bp.productId,
        variant_id: bp.variationId,
        quantity: bp.quantity,
        price: bp.price,
        category_id: bp.productTypeId,
      })),
      quantity: sumByProperty(deviationProductsForCurrentWeekYear, "quantity"),
      total: finances?.total,
      status: billingData.billing.status,
      week,
      year,
    });

    if (!isMealboxBasketChanged && !weekIsActive) {
      await createOrUpdateScheduler({ week, year, delivery: true }).unwrap();
    }

    if (isPausedAccount) {
      const hasAllergenPreferences = billingData?.billing.preferences.some(
        (p) => p.isAllergen,
      );

      const requiredConsentIds: string[] = [
        CONSENT_IDS.DATA_PROCESSING[BRAND_NAME],
      ];

      if (hasAllergenPreferences) {
        requiredConsentIds.push(CONSENT_IDS.ALLERGEN_STORING[BRAND_NAME]);
      }
      showNotification({
        type: "success",
        message: intl.NOTIFICATION_DELIVERY_STARTED,
      });
      if (isSomeUpcomingDeviationEmpty) {
        trigger({
          week,
          year,
          requiredConsentIds,
          overrideDeviations: false,
        });
      }
      router.push(BRAND_PATHS.RESTART_BILLING({ week, year }));
    } else {
      router.push(BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }));
    }
    dispatch(toggleEditModeForWeek({ week, year }));
  };

  return {
    handleSubmitDeviation,
    isSubmitDeviationLoading,
  };
};

const intl = (
  {
    no: {
      GOOD_MORNING: "God morgen",
      GOOD_DAY: "Hei",
      GOOD_AFTERNOON: "God ettermiddag",
      GOOD_EVENING: "God kveld",
      NO_DELIVERIES_BEFORE_START_WEEK: "Du kan ikke bestille før startuke",
      ORDER: "Bestilling",
      NOTIFICATION_DELIVERY_STARTED: "Dine leveranser er gjenopptatt",
    },
    se: {
      GOOD_MORNING: "God morgon",
      GOOD_DAY: "Hej",
      GOOD_AFTERNOON: "God eftermiddag",
      GOOD_EVENING: "God kväll",
      NO_DELIVERIES_BEFORE_START_WEEK: "Du kan inte beställa innan startvecka",
      ORDER: "Beställning",
      NOTIFICATION_DELIVERY_STARTED: "Du har nu leveranser varje vecka!",
    },
    dk: {
      GOOD_MORNING: "Godmorgen",
      GOOD_DAY: "Hej",
      GOOD_AFTERNOON: "God eftermiddag",
      GOOD_EVENING: "God aften",
      NO_DELIVERIES_BEFORE_START_WEEK: "Du kan ikke bestille før startuge",
      ORDER: "Bestilling",
      NOTIFICATION_DELIVERY_STARTED: "Dine leveringer er startet",
    },
  } as const
)[language];
